import { useEffect, useRef, useCallback, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { format, parseISO } from 'date-fns';
import { useDropzone } from 'react-dropzone';
import debounce from 'lodash/debounce';

import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import Message from './Message';

import {
  LogOut,
  Send,
  Lock,
  FileCheck,
  ImagePlus,
  ExternalLink,
  PackageOpen,
  X,
  ClockAlert,
} from 'lucide-react';

import dateFnsLocales from "@/utils/dateFnsLocales";
import { s3Upload } from '@/utils/aws';
import { AboboCrypto } from '@/utils/aboboCrypto';
import { compressImageIfNeeded } from '@/utils/imageCompression';
import { useAuth } from '@/contexts/AuthContext';
import { createSignedUrl, useBranding } from '@utils/api';
import { useMessages } from '@utils/api/messages';
import { useConversationPresence } from '@/utils/hooks/useConversationPresence';
import { toast } from 'sonner';
import PasswordResetNotification from './PasswordResetNotification';
import { useClientBusinessHours } from '@/utils/hooks/useClientBusinessHours';

export default function SecureChat() {
  const { user: me, logout, clientPublicKey, patientPublicKey } = useAuth();
  const { messages, createMessage, mutate } = useMessages();
  const { t, i18n} = useTranslation();
  const [showForms, setShowForms] = useState(false);
  const { branding } = useBranding();
  const { isClientOpen, getFormattedNextOpenTime } = useClientBusinessHours();

  // Subscribe to presence channel for real-time updates
  const conversationId = messages?.[0]?.conversationId;
  const { setTyping, isSubscribed } = useConversationPresence(conversationId, {
    onMessageEvent: () => {
      // Refetch on any thread activity: clinic-sent messages ('outgoing' in
      // the API's clinic-centric direction) and the patient's own sends from
      // other devices ('incoming') both need a refresh here.
      mutate();
    },
  });

  // Typing indicator state - only engage when subscribed
  const isTypingRef = useRef(false);

  // Debounced function to stop typing after 5 seconds of inactivity
  const stopTypingDebounced = useMemo(
    () =>
      debounce(() => {
        if (isTypingRef.current) {
          isTypingRef.current = false;
          setTyping(false);
        }
      }, 5000),
    [setTyping]
  );

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      stopTypingDebounced.cancel();
      if (isTypingRef.current) {
        setTyping(false);
      }
    };
  }, [stopTypingDebounced, setTyping]);

  const handleTypingStart = useCallback(() => {
    // Don't engage typing logic until subscribed
    if (!isSubscribed) return;

    if (!isTypingRef.current) {
      isTypingRef.current = true;
      setTyping(true);
    }
    stopTypingDebounced();
  }, [isSubscribed, setTyping, stopTypingDebounced]);

  const handleTypingStop = useCallback(() => {
    stopTypingDebounced.cancel();
    if (isTypingRef.current) {
      isTypingRef.current = false;
      setTyping(false);
    }
  }, [stopTypingDebounced, setTyping]);


  const toggleForms = () => setShowForms((oldState) => !oldState);
  const formsAvailable = me?.responses?.length > 0 || false;

  const messagesEndRef = useRef<HTMLDivElement>(null);

  interface AttachmentAttributes {
    key: string;
    encryptionKey: string;
    extension: string;
    mimeType: string;
  }

  const defaultNewMessage: { body: string; attachmentAttributes: AttachmentAttributes | null } = {
    body: "",
    attachmentAttributes: null
  };

  const [newMessage, setNewMessage] = useState(defaultNewMessage);
  const [loading, setLoading] = useState(false);
  const [closedAlertDismissed, setClosedAlertDismissed] = useState(false);

  // Track message content in a ref for the subscription effect
  const newMessageRef = useRef(newMessage);
  newMessageRef.current = newMessage;

  // When presence subscription becomes ready, send typing event if there's text
  useEffect(() => {
    if (
      isSubscribed &&
      newMessageRef.current.body.trim().length > 0 &&
      !isTypingRef.current
    ) {
      isTypingRef.current = true;
      setTyping(true);
      stopTypingDebounced();
    }
  }, [isSubscribed, setTyping, stopTypingDebounced]);

  useEffect(() => {
    if (me?.languagePreference) {
      i18n.changeLanguage(me.languagePreference);
    }
  }, [me?.languagePreference, i18n]);

  useEffect(() => {
    if (
      !messagesEndRef.current ||
      typeof messagesEndRef.current === 'undefined'
    )
      return;
    messagesEndRef.current.scrollTop = messagesEndRef.current.scrollHeight;
  }, [messages]);

  const handleInputChange = (e) => {
    const inputValue = e.target.value;
    setNewMessage((oldState) => ({...oldState, body: inputValue}));
    // Emit typing indicator when user types
    if (inputValue.length > 0) {
      handleTypingStart();
    }
  };

  const handleSubmit = async (e) => {
    e.stopPropagation();

    // Stop typing indicator when message is sent
    handleTypingStop();

    if (!clientPublicKey || !patientPublicKey) {
      toast.error(t('toasts.errorTitle'), { description: t('toasts.encryptionKeysUnavailable') });
      return;
    }

    AboboCrypto.encryptPayloadAndWrapKey(newMessage.body, [
      clientPublicKey,
      patientPublicKey,
    ]).then(
      (result: [Uint8Array, Uint8Array[]]) => {
        const [encryptedPayload, [clientWrappedKey, patientWrappedKey]] = result;
        const encodedPayload =
          AboboCrypto.uInt8ArrayToBase64(encryptedPayload);
        const encodedClientKey =
          AboboCrypto.uInt8ArrayToBase64(clientWrappedKey);
        const encodedPatientKey =
          AboboCrypto.uInt8ArrayToBase64(patientWrappedKey);
        const payload = {
          body: encodedPayload,
          clientAccessKey: encodedClientKey,
          patientAccessKey: encodedPatientKey,
          attachmentAttributes: newMessage.attachmentAttributes
        };

        createMessage(payload);
        setNewMessage(defaultNewMessage);
      }
    );
  };

  const handleFileRejected = useCallback(() => {
      toast.error(
        t('errors.uploadRejected.title'),
        { description: `${t('errors.uploadRejected.message')} ${t('errors.uploadRejected.details')}` }
      );
    }, [t]);

  const onDrop = useCallback(async ([file]) => {
    setLoading(true)
    
    if (typeof file === 'undefined') {
      setLoading(false);
      handleFileRejected();
      return;
    }
    
    try {
      // Compress image if needed
      const processedFile = await compressImageIfNeeded(file);
      
      // Check if processed file is still too large
      if (processedFile.size > 5000000) {
        setLoading(false);
        toast.error(
          t('errors.uploadRejected.title'),
          { description: t('errors.uploadRejected.message') + ' ' + t('errors.uploadRejected.details') }
        );
        return;
      }
      
      const extension = processedFile.name.split('.').pop() ?? '';
      const signedUrlPromise = createSignedUrl(extension)
      const reader = new FileReader()

      reader.onload = async () => {
        if (!clientPublicKey) {
          toast.error(t('toasts.errorTitle'), { description: t('toasts.clientKeyUnavailable') });
          setLoading(false);
          return;
        }

        const result = await AboboCrypto.encryptPayloadAndWrapKey(reader.result, [clientPublicKey]) as [Uint8Array, Uint8Array[]];
        const [encryptedFile, [wrappedKey]] = result;
        const encodedKey = AboboCrypto.uInt8ArrayToBase64(wrappedKey)
        const signedUrlResult = await signedUrlPromise;
        if (!signedUrlResult?.data) {
          throw new Error('Failed to get signed URL');
        }
        const { id, url } = signedUrlResult.data;

        s3Upload(url, encryptedFile, processedFile.type).then(() => {
          setNewMessage((oldState) => ({
            ...oldState,
            attachmentAttributes: {
              key: id,
              encryptionKey: encodedKey,
              extension, 
              mimeType: processedFile.type
            }
          }));
          setLoading(false);
          toast.success(
            t('secure.fileUpload.uploadSuccessTitle'),
            { description: t('secure.fileUpload.uploadSuccessMessage') }
          );
        }).catch((err) => {
          setLoading(false);
          toast.error(
            t('secure.fileUpload.uploadErrorTitle'),
            { description: t('secure.fileUpload.uploadErrorMessage', {statusCode: err.status}) }
          );
        })
      }

      reader.readAsArrayBuffer(processedFile)
    } catch (error) {
      setLoading(false);
      console.error('File processing error:', error);
      toast.error(
        t('errors.uploadRejected.title'),
        { description: t('toasts.fileProcessingFailed') }
      );
    }
  }, [t, handleFileRejected, clientPublicKey]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    maxSize: 50000000, // 50MB - allow large files to reach compression
    onDrop,
    accept: {
      'image/png': ['.png'],
      'image/jpeg': ['.jpeg', '.jpg'],
      'application/pdf': ['.pdf'],
    },
    multiple: false,
  });

  const renderUploadButtonIcon = () => {
    if (isDragActive) {
      return <PackageOpen className="h-5 w-5" />;
    } else if (newMessage.attachmentAttributes?.key) {
      return <FileCheck className="h-5 w-5" />;
    } else {
      return <ImagePlus className="h-5 w-5" />;
    }
  };

  const getClosedMessage = () => {
    const formattedTime = getFormattedNextOpenTime();
    
    if (!formattedTime) {
      return t('secure.clientClosed.message');
    }
    
    return t('secure.clientClosed.message', { time: formattedTime });
  };

  if (me) {
    return (
      <div className="flex flex-col p-4 pb-2 bg-white border-b-2 w-full max-w-xl flex-grow-1 max-h-[50rem] rounded-2xl mx-auto relative">
        {/* Header */}
        <div className="flex flex-col w-full border-b pb-2">
          {/* Logo */}
          {branding?.logo && (
            <div className="flex justify-center mb-4">
              <img
                src={branding.logo}
                alt={`${branding.name}'s Logo`}
                className="object-contain h-10 sm:h-20 bg-white"
              />
            </div>
          )}
          {/* User controls */}
          <div className="flex items-center justify-between w-full">
            <h2 className="px-2 text-sm sm:text-xl font-medium">
              {t('secure.welcome')} {me?.displayFirstName}
            </h2>
            <div className="flex items-center gap-2">
              {formsAvailable && (
                <div className="relative">
                  <Button
                    size="sm"
                    onClick={toggleForms}
                    style={{
                      backgroundColor: branding?.buttonColor,
                      color: branding?.fontColor,
                    }}
                  >
                    {showForms ? t('secure.messages') : t('secure.forms')}
                  </Button>
                  {!showForms && (
                    <Badge 
                      className="absolute -right-2 -top-2 bg-red-500 text-white text-xs px-2 py-0.5 rounded-full"
                    >
                      {me?.responses?.length}
                    </Badge>
                  )}
                </div>
              )}
              <Button
                size="sm"
                variant="destructive"
                onClick={logout}
                className="flex items-center gap-2"
              >
                {t('secure.exit')}
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
        {/* Next Appointment */}
        {me?.nextAppointment && (
          <div className="text-left p-2 bg-gray-50 w-full rounded">
            <p className="text-xs sm:text-base text-gray-600">
              {t('secure.upcomingAppointment')}:{" "}
              {format(parseISO(me?.nextAppointment), "ccc, MMM dd yyyy", { locale: dateFnsLocales[i18n.language] })} |{" "}
              {format(parseISO(me?.nextAppointment), "h:mm a", { locale: dateFnsLocales[i18n.language] })}
            </p>
          </div>
        )}
        {/* Forms Section or Messages Container */}
        {showForms && me?.responses.length > 0 ? (
          <div className="flex-grow w-full bg-white flex flex-col gap-2 mt-2 overflow-y-auto">
            <h3 className="text-md sm:text-lg font-semibold text-center">
              {t('secure.pendingFormsTitle', {count: me.responses.length})}
            </h3>
            {me.responses.map((response) => (
              <div key={response.id} className="p-3 bg-amber-50 border border-amber-200 rounded-md flex items-center justify-between gap-2">
                <div className="text-sm sm:text-md font-medium truncate flex-grow">{response.title}</div>
                <a
                  target="blank"
                  href={`https://${window.location.host}/forms/${response.id}?token=${me.formAccessToken}`}
                >
                  <Button
                    size="sm"
                    className="flex items-center gap-2"
                  >
                    {t('secure.openForm')}
                    <ExternalLink className="h-4 w-4" />
                  </Button>
                </a>
              </div>
            ))}
          </div>
        ) : (
          <>
            {/* Messages Container */}
            <div className="flex-grow w-full bg-white relative overflow-hidden">
              {/* Clinic Closed Alert */}
              {!isClientOpen && !closedAlertDismissed && (
                <Alert className="absolute top-0 left-0 right-0 z-10 bg-amber-50/90 border-amber-200 border-t-0 rounded-t-none">
                  <ClockAlert className="h-4 w-4" />
                  <AlertTitle className="flex items-center justify-between">
                    {t('secure.clientClosed.title')}
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-4 w-4 p-0 hover:bg-transparent"
                      onClick={() => setClosedAlertDismissed(true)}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </AlertTitle>
                  <AlertDescription>
                    {getClosedMessage()}
                  </AlertDescription>
                </Alert>
              )}
              {/* Messages */}
              <div ref={messagesEndRef} className="absolute inset-0 overflow-y-auto flex flex-col-reverse gap-3" style={{paddingTop: !isClientOpen && !closedAlertDismissed ? '80px' : '0'}}>
                {(!messages || (Array.isArray(messages) && messages.length === 0)) && (
                  <div className="text-center p-4">
                    <p className="text-gray-500">{t('secure.messaging.titles.noMessages')}</p>
                  </div>
                )}
                {Array.isArray(messages) && messages.map((message) => (
                  <Message key={message.id} message={message} />
                ))}
              </div>
            </div>
            <Separator className="mb-2" />
            {/* Message Input */}
            <div className="bg-white w-full">
              <div {...getRootProps()} className="flex items-stretch h-20 w-full">
                {/* File Upload Button */}
                <div>
                  <input {...getInputProps()} />
                  <Button
                    variant="outline"
                    size="lg"
                    className="h-full w-10 rounded-r-none border-r-0 text-gray-500"
                    loading={loading}
                  >
                    {renderUploadButtonIcon()}
                  </Button>
                </div>
                {/* Text Input or Drop Zone */}
                {isDragActive ? (
                  <div className="flex-1 flex items-center justify-center border border-dashed border-gray-300 bg-gray-50">
                    <p className="text-gray-600">{t('secure.fileUpload.dropHere')}</p>
                  </div>
                ) : (
                  <textarea
                    value={newMessage.body}
                    onChange={handleInputChange}
                    placeholder={newMessage.attachmentAttributes?.key ? t('secure.placeholderWithFile') : t('secure.placeholder')}
                    className="flex-1 p-2 border-1 resize-none focus:outline-none focus:ring-0 bg-white text-base"
                    onClick={(e) => e.stopPropagation()}
                  />
                )}
                {/* Send Button */}
                <Button
                  onClick={handleSubmit}
                  disabled={newMessage.body.length < 1 || loading}
                  size="lg"
                  className="h-full w-10 rounded-l-none border-l-0"
                >
                    <Send className="h-5 w-5" />
                </Button>
              </div>
              {/* Encryption Notice */}
              <div className="flex items-center justify-center mt-2 gap-2">
                <Lock className="size-3 sm:size-4 text-gray-600" />
                <span className="text-gray-600 text-xs sm:text-sm">{t('secure.encrypted')}</span>
              </div>
            </div>
          </>
        )}
        <PasswordResetNotification />
      </div>
    );
  }

  return null;
}
